import React from "react";
import "./App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "sonner";
import { AuthProvider } from "./lib/auth";
import { BrandingProvider } from "./lib/branding";
import ProtectedRoute from "./components/ProtectedRoute";
import AppShell from "./components/AppShell";
import Login from "./pages/Login";
import Register from "./pages/Register";
import ForgotPassword from "./pages/ForgotPassword";
import ResetPassword from "./pages/ResetPassword";
import Onboarding from "./pages/Onboarding";
import Dashboard from "./pages/Dashboard";
import Wallets from "./pages/Wallets";
import Providers from "./pages/Providers";
import Transactions from "./pages/Transactions";
import Reports from "./pages/Reports";
import Users from "./pages/Users";
import Clients from "./pages/Clients";
import Closures from "./pages/Closures";
import ClosureDetail from "./pages/ClosureDetail";
import Categories from "./pages/Categories";
import Settings from "./pages/Settings";
import AuditLog from "./pages/AuditLog";
import Team from "./pages/Team";
import VistaSede from "./pages/VistaSede";
import { useAuth } from "./lib/auth";

function Shelled({ children }) {
    return (
        <ProtectedRoute>
            <AppShell>{children}</AppShell>
        </ProtectedRoute>
    );
}

// Route accessibili solo a owner + agency_manager (non operator).
// Se l'utente non ha il permesso, viene reindirizzato a /transactions.
function ManagerRoute({ children }) {
    const { user } = useAuth();
    if (user === null) return null; // loading
    if (user && user.role === "operator") {
        return <Navigate to="/transactions" replace />;
    }
    return <Shelled>{children}</Shelled>;
}

// Redirect intelligente: tutti puntano a /dashboard (operator vede la versione ridotta).
function HomeRedirect() {
    return <Navigate to="/dashboard" replace />;
}

function App() {
    return (
        <div className="App">
            <AuthProvider>
                <BrandingProvider>
                    <BrowserRouter>
                    <Toaster
                        position="top-right"
                        toastOptions={{
                            style: {
                                borderRadius: 0,
                                border: "1px solid #09090b",
                                fontFamily: "Chivo, sans-serif",
                            },
                        }}
                    />
                    <Routes>
                        <Route path="/" element={<HomeRedirect />} />
                        <Route path="/login" element={<Login />} />
                        <Route path="/register" element={<Register />} />
                        <Route path="/forgot-password" element={<ForgotPassword />} />
                        <Route path="/reset-password" element={<ResetPassword />} />
                        <Route path="/vista-sede/:token" element={<VistaSede />} />
                        <Route
                            path="/onboarding"
                            element={
                                <ProtectedRoute>
                                    <Onboarding />
                                </ProtectedRoute>
                            }
                        />
                        <Route
                            path="/dashboard"
                            element={
                                <Shelled>
                                    <Dashboard />
                                </Shelled>
                            }
                        />
                        <Route
                            path="/wallets"
                            element={
                                <ManagerRoute>
                                    <Wallets />
                                </ManagerRoute>
                            }
                        />
                        <Route
                            path="/transactions"
                            element={
                                <Shelled>
                                    <Transactions />
                                </Shelled>
                            }
                        />
                        <Route
                            path="/providers"
                            element={
                                <Shelled>
                                    <Providers />
                                </Shelled>
                            }
                        />
                        <Route
                            path="/clients"
                            element={
                                <Shelled>
                                    <Clients />
                                </Shelled>
                            }
                        />
                        <Route
                            path="/closures"
                            element={
                                <ManagerRoute>
                                    <Closures />
                                </ManagerRoute>
                            }
                        />
                        <Route
                            path="/closures/:id"
                            element={
                                <ManagerRoute>
                                    <ClosureDetail />
                                </ManagerRoute>
                            }
                        />
                        <Route
                            path="/audit-log"
                            element={
                                <ManagerRoute>
                                    <AuditLog />
                                </ManagerRoute>
                            }
                        />
                        <Route
                            path="/categories"
                            element={
                                <ManagerRoute>
                                    <Categories />
                                </ManagerRoute>
                            }
                        />
                        <Route
                            path="/reports"
                            element={
                                <ManagerRoute>
                                    <Reports />
                                </ManagerRoute>
                            }
                        />
                        <Route
                            path="/users"
                            element={
                                <ManagerRoute>
                                    <Users />
                                </ManagerRoute>
                            }
                        />
                        <Route
                            path="/team"
                            element={
                                <ManagerRoute>
                                    <Team />
                                </ManagerRoute>
                            }
                        />
                        <Route
                            path="/settings"
                            element={
                                <Shelled>
                                    <Settings />
                                </Shelled>
                            }
                        />
                        <Route
                            path="/settings/:tab"
                            element={
                                <Shelled>
                                    <Settings />
                                </Shelled>
                            }
                        />
                        <Route path="*" element={<HomeRedirect />} />
                    </Routes>
                    </BrowserRouter>
                </BrandingProvider>
            </AuthProvider>
        </div>
    );
}

export default App;
